import { REGION_STEPS, regionKToKm, kmToRegionK } from "./talk-geo.js";
import type { T } from "./talk-row.js";

export interface TalkTabCtx {
  t: T;
  scope: "area" | "region";
  regionKm: number;
  /** Persistent feed element (owned by a TalkFeed controller), mounted in the feed slot. */
  feedEl: HTMLElement;
  /** Zone picker element injected by the app; rendered above the scope toggle. */
  zonePickerEl: HTMLElement;
  /** When false the composer textarea + send button are disabled and a hint is shown. */
  canPost: boolean;
  onScope: (scope: "area" | "region") => void;
  onRegionKm: (km: number) => void;
  onSend: (text: string) => Promise<void>;
  /** Draft text to seed the composer (preserved across scope/tab switches). */
  draft?: string;
  /** Report composer edits up so the draft survives a re-render. */
  onDraft?: (text: string) => void;
}

const COMPOSER_MAX_H = 120; // px — ~5 lines, then the textarea scrolls

/** Talk tab chrome: scope toggle + (region) size slider + the feed + an auto-grow composer.
 *  The chronological feed itself is owned by a persistent TalkFeed controller (ctx.feedEl). */
export function talkTab(ctx: TalkTabCtx): HTMLElement {
  const { t } = ctx;
  const root = document.createElement("section");
  root.className = "screen talk-tab";

  // --- Zone picker (above scope toggle) ---
  root.append(ctx.zonePickerEl);

  // --- Scope toggle ---
  const toggle = document.createElement("div");
  toggle.className = "talk-scope";
  toggle.setAttribute("role", "tablist");
  for (const scope of ["area", "region"] as const) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `talk-scope__btn${ctx.scope === scope ? " talk-scope__btn--active" : ""}`;
    b.setAttribute("data-scope", scope);
    b.setAttribute("aria-selected", String(ctx.scope === scope));
    b.textContent = t(`talk.scope.${scope}`);
    b.addEventListener("click", () => ctx.onScope(scope));
    toggle.append(b);
  }
  root.append(toggle);

  // --- Region size slider (region scope only) ---
  if (ctx.scope === "region") {
    const wrap = document.createElement("label");
    wrap.className = "talk-region-slider";
    wrap.setAttribute("data-region-slider", "");
    const caption = document.createElement("span");
    caption.className = "bx-microlabel";
    caption.textContent = t("talk.region.size", { km: ctx.regionKm });
    const slider = document.createElement("input");
    slider.type = "range";
    slider.min = "0";
    slider.max = String(REGION_STEPS.length - 1);
    slider.step = "1";
    slider.value = String(REGION_STEPS.findIndex((s) => s.k === kmToRegionK(ctx.regionKm)));
    slider.addEventListener("change", () => {
      const step = REGION_STEPS[Number(slider.value)];
      ctx.onRegionKm(regionKToKm(step.k));
    });
    wrap.append(caption, slider);
    root.append(wrap);
  }

  // --- Feed (owned by the persistent TalkFeed controller) ---
  root.append(ctx.feedEl);

  // --- Composer (auto-grow; Enter sends, Shift+Enter newlines) ---
  const composer = document.createElement("form");
  composer.className = "talk-composer";
  composer.addEventListener("submit", (e) => e.preventDefault());

  const input = document.createElement("textarea");
  input.setAttribute("data-message-input", "");
  input.className = "talk-composer__input";
  input.rows = 1;
  input.placeholder = t("talk.composer.placeholder");
  input.value = ctx.draft ?? ""; // seed the preserved draft

  const autogrow = () => {
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, COMPOSER_MAX_H)}px`;
  };
  input.addEventListener("input", () => { autogrow(); ctx.onDraft?.(input.value); });

  const send = document.createElement("button");
  send.type = "button";
  send.className = "talk-composer__send bx-btn bx-btn--primary";
  send.setAttribute("data-action", "send-message");
  send.textContent = t("talk.composer.send");

  if (!ctx.canPost) {
    input.disabled = true;
    send.disabled = true;
    const hint = document.createElement("p");
    hint.className = "talk-composer__hint";
    hint.textContent = t("talk.compose.noCell");
    composer.append(input, send, hint);
  } else {
    const submit = async () => {
      const text = input.value.trim();
      if (!text) return;
      try {
        await ctx.onSend(text);
        input.value = ""; // clear only on success; keep the text in the box to retry on failure
        ctx.onDraft?.(""); // clear the preserved draft too
        autogrow();
      } catch (err) {
        console.warn("[talk] send failed:", err);
      }
    };
    send.addEventListener("click", () => void submit());
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void submit();
      }
    });
    composer.append(input, send);
  }

  root.append(composer);

  return root;
}
